import { HttpErrorResponse,HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import { EMPTY, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { Capacitor } from '@capacitor/core';

import { environment } from '../../environments/environment';
import { AuthService } from '@app/services/auth/auth.service';

/**
 * Auth interceptor (functional style)
 *
 * Why this exists:
 *  - Attach the bearer token to every request that targets *our API*.
 *  - If the API tells us the token is invalid/expired (401), log out and
 *    send the user back to /signin.
 *
 * Notes:
 *  - We DO NOT modify requests to other origins or to local assets.
 *  - We DO NOT swallow errors — everything still reaches the caller.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  // Normalize base once (ensure trailing slash)
  const apiBase = normalizeBase(environment.apiBaseUrl);

  // Is this request going to our API? (keeps images, assets, 3rd-party calls untouched)
  const isApiCall = req.url.startsWith(apiBase);

  const isNative = Capacitor.isNativePlatform();
  const mode = environment.auth?.mode ?? 'auto'; // check if we have env over ride

  // token transport if forced OR (auto + native)
  const useTokenAuth = (mode === 'token') || (mode === 'auto' && isNative);

  // session transport if forced OR (auto + web)
  const useSessionCookies = (mode === 'session') || (mode === 'auto' && !isNative);

  let modifiedReq = req;

  const logoutPath = 'account/web/logout';
  const isLogoutCall = isApiCall && req.url.includes(logoutPath);


  // If we have a token and it's an API call, add the Authorization header
  const token = auth.token;
  const maybeAuthedReq = (isApiCall && token)
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  if (isApiCall) {
    const token = auth.token;

    if (useTokenAuth && token) {
      // 🔑 Token mode → attach Bearer token
      modifiedReq = modifiedReq.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
        // Ensure we don't accidentally send cookies in token mode
        withCredentials: false,
      });
    } else if (useSessionCookies) {
      // 🍪 Session mode → rely on cookies
      modifiedReq = modifiedReq.clone({
        withCredentials: true,
      });
    }
  }

  return next(modifiedReq).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        const isLogoutCall = isApiCall && req.url.includes('/account/web/logout');

        if (isApiCall && !isLogoutCall && err.status === 401) {
           void auth.logout('http_401', {
              url: req.url,
              method: req.method,
              status: err.status,
              statusText: err.statusText,
              currentRoute: router.url,
            }); // logout will nav to sign in page

            return EMPTY;
        }
      }
      return throwError(() => err);
    })
  );
  /*
  return next(maybeAuthedReq).pipe(
    // Centralised error handling
    catchError((err: unknown) => {
      // Only react to HTTP errors
      if (err instanceof HttpErrorResponse) {
        // If our API says 401, treat it as an invalid session
        if (isApiCall && err.status === 401) {
          // Side-effect here is OK: we're at the app boundary
          // Fire and forget; the navigation will occur after logout resolves
          void auth.logout();
          void router.navigateByUrl('/signin', { replaceUrl: true });
        }
      }
      // Re-throw so callers still see their errors
      return throwError(() => err);
    })
  );
  */
};

/** Ensure the base URL ends with a slash so `startsWith` matches predictably. */
function normalizeBase(url: string): string {
  return url.endsWith('/') ? url : url + '/';
}
